import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";

/**
 * LT-16 Part 2d — Brand enable/disable.
 *
 * Fetches `/settings/brands` once and exposes `enabled` (the map),
 * `activeBrands` (array of enabled keys), and `showPicker` (true iff
 * more than one brand is enabled). Callers that only need the picker
 * gate should destructure `showPicker` — asking "which brand?" of
 * a workspace with one enabled brand is worse than not asking.
 */
const BRAND_LABEL = {
  lux_construction: "Lux Construction",
  lux_developments: "Lux Developments",
};

export function useEnabledBrands() {
  const [enabled, setEnabled] = useState({
    lux_construction: true,
    lux_developments: true,
  });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/settings/brands");
      const e = r?.data?.brands_enabled || {};
      setEnabled({
        lux_construction: !!e.lux_construction,
        lux_developments: !!e.lux_developments,
      });
    } catch (_) {
      // Silent fallback — both enabled. A broken /brands read should
      // not block picker rendering; failing closed hides all brands.
      setEnabled({ lux_construction: true, lux_developments: true });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const activeBrands = Object.entries(enabled)
    .filter(([_, on]) => on)
    .map(([k]) => k);

  return {
    enabled,
    activeBrands,
    loading,
    // `showPicker` — the whole point of the toggle. Hide when there's
    // only one option; render silently.
    showPicker: activeBrands.length > 1,
    labelFor: (key) => BRAND_LABEL[key] || key,
    refetch: load,
  };
}
